import { api } from "../api";

function getScopedSelectedPersona() {
  try {
    return localStorage.getItem("selectedProfileId") || null;
  } catch {
    return null;
  }
}

function getPendingNewAnamnesePerId() {
  return getScopedSelectedPersona();
}

function clearPendingNewAnamnesePerId() {
  // selectedProfileId é a fonte única e permanece definida
}

function extractApiError(error, fallback = "Falha desconhecida") {
  // tenta puxar mensagem padronizada da API; ajuste as chaves se sua API usa outro formato
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function getLastInsertId(data) {
  // Tenta extrair o ID em formatos variados de resposta da API.
  if (!data) return null;
  return (
    data?.data?.anm_id ??
    data?.data?.id ??
    data?.anm_id ??
    data?.id ??
    data?.last_insert_id ??
    data?.resp?.last_insert_id ??
    data?.data?.resp?.last_insert_id ??
    null
  );
}

/**
 * Persiste o ID da anamnese vinculado ao usuário, para evitar vazamento entre sessões.
 */
function saveCurrentAnamnesis(id, usrId) {
  if (!id) return;
  localStorage.setItem(
    "currentAnamnesis",
    JSON.stringify({ id: id.toString(), usrId: usrId ? String(usrId) : null }),
  );
}

/**
 * Lê o ID da anamnese do storage, validando que pertence ao usuário informado.
 */
export function readCurrentAnamnesis(usrId) {
  const raw = localStorage.getItem("currentAnamnesis");
  if (!raw || raw === "null" || raw === "undefined") return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.id) {
      if (parsed.usrId == null || String(parsed.usrId) === String(usrId)) {
        const idNum = Number(parsed.id);
        return Number.isInteger(idNum) && idNum > 0 ? idNum : null;
      }
      return null;
    }
    const num = Number(parsed ?? raw);
    if (Number.isInteger(num) && num > 0) {
      return num;
    }
    return null;
  } catch {
    const num = Number(raw);
    if (Number.isInteger(num) && num > 0) {
      return num;
    }
    return null;
  }
}

export async function registerUserWithAnswers({ setIsProcessing, usr_id }) {
  setIsProcessing(true);

  try {
    const userAnswersRaw = localStorage.getItem("userAnswers") || "{}";
    let parsedAnswers;

    // JSON bem formado
    try {
      parsedAnswers = JSON.parse(userAnswersRaw);
    } catch {
      throw new Error("Dados do questionário corrompidos. Reinicie o processo.");
    }

    if (!parsedAnswers || typeof parsedAnswers !== "object") {
      throw new Error("Dados do questionário inválidos.");
    }

    const origin = parsedAnswers.origin; // "my-baby" | "my-kids" | "adulto" | undefined
    const name = parsedAnswers["252"]?.answer ?? null;

    // mapeie explicitamente o sexo se houver mais valores
    const genderAnswer = parsedAnswers["254"]?.answer;
    const gender = genderAnswer === 189 ? 1 : genderAnswer === 190 ? 2 : null;
    const anm_id = readCurrentAnamnesis(usr_id);

    // ===== MY BABY =====
    if (origin === "my-baby") {
      const babyEvaluation = JSON.parse(localStorage.getItem("babyEvaluation") || "null");
      const per_id = getPendingNewAnamnesePerId();

      try {
        const resp = await api.post("anamnesisSave", {
          name,
          gender,
          anamnesis: userAnswersRaw,   // string crua mesmo, se o backend espera string
          evaluation: babyEvaluation,  // objeto; ajuste se o backend espera string
          origin: "my-baby",
          per_id: per_id || undefined,
          anm_id: anm_id || undefined,
        });

        const id = getLastInsertId(resp.data);
        if (id) saveCurrentAnamnesis(id, usr_id);
        // Consome o per_id apenas quando originou da lista de perfis
        clearPendingNewAnamnesePerId();

        window.location.href = "/solucoes-baby";
        return;
      } catch (error) {
        const msg = extractApiError(error, "Falha desconhecida ao salvar anamnese (baby).");
        console.error("[my-baby] Falha ao salvar:", msg);
        throw new Error(msg);
      }
    }

    // ===== MY KIDS =====
    if (origin === "my-kids") {
      const kidsEvaluation = JSON.parse(localStorage.getItem("kidsEvaluation") || "null");
      const per_id = getPendingNewAnamnesePerId();

      try {
        const resp = await api.post("anamnesisSave", {
          name,
          gender,
          anamnesis: userAnswersRaw,
          evaluation: kidsEvaluation,
          origin: "my-kids",
          per_id: per_id || undefined,
          anm_id: anm_id || undefined,
        });

        const id = getLastInsertId(resp.data);
        if (id) saveCurrentAnamnesis(id, usr_id);
        clearPendingNewAnamnesePerId();

        window.location.href = "/solucoes-kids";
        return;
      } catch (error) {
        const msg = extractApiError(error, "Falha desconhecida ao salvar anamnese (kids).");
        console.error("[my-kids] Falha ao salvar:", msg);
        throw new Error(msg);
      }
    }

    // ===== ADULTO / POCKET =====
    const adultEvaluation = JSON.parse(localStorage.getItem("adultEvaluation") || "null");
    const per_id = getPendingNewAnamnesePerId();
    try {
      const resp = await api.post("anamnesisSave", {
        name,
        gender,
        anamnesis: userAnswersRaw,
        evaluation: adultEvaluation,
        origin: "adulto",
        per_id: per_id || undefined,
        anm_id: anm_id || undefined,
      });

      // console.log("Anamnese adulto salva, resposta:", resp.data);
      // return;
      const id = getLastInsertId(resp.data);
      if (id) saveCurrentAnamnesis(id, usr_id);
      clearPendingNewAnamnesePerId();

      // se não veio origin, cai no mapa-da-saude; se vier "adulto", idem
      const fallback = origin === "adulto" ? "mapa-da-saude" : "checkout";
      window.location.href = `${process.env.PUBLIC_URL || ""}/${fallback}`;
      return;
    } catch (error) {
      const msg = extractApiError(error, "Falha desconhecida ao salvar anamnese (adulto).");
      console.error("[adult] Falha ao salvar:", msg);
      throw new Error(msg);
    }
    
  } catch (error) {
    // mensagem geral (se quiser, troque o texto)
    console.error("Erro ao salvar a anamnese:", error);
    throw error;
  } finally {
    setIsProcessing(false);
  }
}
